import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { dashboardAPI } from '@/lib/api';
import { Receipt, ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react';

const inr = (n) => `₹${Math.round(n || 0).toLocaleString('en-IN')}`;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const Change = ({ cur, prev, pct }) => {
  if (!prev && !cur) return null;
  if (!prev) return <span className="text-[10px] font-bold text-indigo-400">NEW</span>;
  if (cur === prev) return <span className="text-xs text-muted-foreground flex items-center"><Minus className="w-3 h-3" />0%</span>;
  const up = cur > prev;
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return <span className={`text-xs font-bold flex items-center ${up ? 'text-rose-400' : 'text-emerald-400'}`}><Icon className="w-3.5 h-3.5" />{Math.abs(pct)}%</span>;
};

export const CategorySummaryCard = ({ ym: fixedYm }) => {
  const navigate = useNavigate();
  const now = new Date();
  const [ownYm, setYm] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
  const ym = fixedYm || ownYm;
  const [data, setData] = useState(null);

  useEffect(() => {
    const [year, month] = ym.split('-').map(Number);
    dashboardAPI.getCategorySummary({ month, year }).then(r => setData(r.data)).catch(() => setData('error'));
  }, [ym]);

  const prevLabel = data && data !== 'error' ? MONTHS[(data.month + 10) % 12] : '';
  const max = data && data !== 'error' ? Math.max(1, ...data.categories.map(c => Math.max(c.current, c.previous))) : 1;

  return (
    <Card className="border border-border rounded-md bg-card" data-testid="category-summary-card">
      <CardHeader className="pb-2 flex flex-row items-center justify-between gap-3 space-y-0">
        <CardTitle className="text-lg font-heading font-bold uppercase tracking-tight text-card-foreground flex items-center gap-2 cursor-pointer hover:text-primary transition-colors" onClick={() => navigate('/expenses')} data-testid="category-summary-heading">
          <Receipt className="w-5 h-5" /> Expenses by Category
        </CardTitle>
        {!fixedYm && <input type="month" value={ym} onChange={(e) => e.target.value && setYm(e.target.value)}
          className="bg-muted/50 border border-border rounded-md h-9 px-2 text-sm" data-testid="category-summary-month" />}
      </CardHeader>
      <CardContent>
        {data === 'error' ? <p className="text-sm text-rose-400" data-testid="category-summary-error">Could not load category totals. Please refresh.</p> : !data ? <p className="text-sm text-muted-foreground">Loading...</p> : (
          <>
            <div className="flex items-end justify-between mb-4 pb-3 border-b border-border">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">{MONTHS[data.month - 1]} {data.year}</p>
                <p className="text-2xl font-bold text-foreground font-mono" data-testid="category-summary-total">{inr(data.total_current)}</p>
              </div>
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">vs {prevLabel}</p>
                <div className="flex items-center gap-2 justify-end">
                  <span className="text-sm font-mono text-muted-foreground" data-testid="category-summary-prev-total">{inr(data.total_previous)}</span>
                  <Change cur={data.total_current} prev={data.total_previous}
                    pct={data.total_previous ? Math.round((data.total_current - data.total_previous) / data.total_previous * 1000) / 10 : null} />
                </div>
              </div>
            </div>
            {data.categories.length === 0 && <p className="text-sm text-muted-foreground text-center py-4" data-testid="category-summary-empty">No expenses in these two months</p>}
            {data.categories.length > 0 && (
              <div className="flex justify-end gap-2 text-[10px] uppercase tracking-widest text-muted-foreground font-bold mb-1">
                <span>{MONTHS[data.month - 1]}</span><span className="w-20 text-right">{prevLabel}</span><span className="w-12 text-right">Change</span>
              </div>
            )}
            <div className="space-y-2.5">
              {data.categories.map((c, i) => (
                <div key={c.category} data-testid={`category-summary-row-${i}`}>
                  <div className="flex items-center justify-between gap-3 text-sm">
                    <span className="text-foreground truncate">{c.category}</span>
                    <span className="flex items-center gap-2 shrink-0">
                      <span className="font-mono font-bold text-foreground">{inr(c.current)}</span>
                      <span className="font-mono text-xs text-muted-foreground w-20 text-right">{inr(c.previous)}</span>
                      <span className="w-12 flex justify-end"><Change cur={c.current} prev={c.previous} pct={c.change_pct} /></span>
                    </span>
                  </div>
                  <div className="mt-1 h-1.5 rounded-full bg-muted overflow-hidden relative">
                    <div className="absolute inset-y-0 left-0 bg-muted-foreground/30 rounded-full" style={{ width: `${(c.previous / max) * 100}%` }} />
                    <div className="absolute inset-y-0 left-0 bg-indigo-500 rounded-full transition-[width] duration-500" style={{ width: `${(c.current / max) * 100}%`, opacity: 0.9 }} />
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
